import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SectionCard } from "./SectionCard";
import { PullQuote } from "./PullQuote";
import { MarkdownTable } from "./MarkdownTable";
import { HighlightList, type HighlightTone } from "./HighlightList";
import { DefinitionRow } from "./DefinitionRow";
import { InsightCard, type InsightTone } from "./InsightCard";
import { QuoteCard } from "./QuoteCard";
import { StepFlow } from "./StepFlow";
import { CompareCard } from "./CompareCard";

/**
 * Hebrew-safe rich markdown renderer for Lilach's guide pages.
 *
 * Beyond plain markdown → HTML, this renderer detects structural patterns
 * in the source and renders each as the appropriate editorial component:
 *
 *   - GFM tables (`| col | col |`) → <MarkdownTable>
 *   - Lists where every item starts with the same emoji marker (✅ ❌ ⚡
 *     🎯 🔒 etc.) → <HighlightList> with tone-coded badges
 *   - "**term**: body" lines inside a list → <DefinitionRow>
 *   - Blockquotes → <PullQuote>
 *   - Plain markdown blocks → typed typography (h1–h4, p, ul, ol, code)
 *
 * The body is split on horizontal-rule lines (`---`); each chunk wraps in
 * <SectionCard>. That gives every guide the editorial section feel
 * without any per-guide work.
 */

export type GuideMarkdownProps = {
  /** The raw markdown body, as it appears in each guide's SECTIONS[].body. */
  body: string;
  /** Optional className applied to the outer stack. */
  className?: string;
  /** When true, wraps each chunk in a <SectionCard> for the legacy
   *  card-chrome look. Default false — content flows directly on the
   *  page background so it reads as a magazine spread. */
  framed?: boolean;
};

export function GuideMarkdown({
  body,
  className,
  framed = false,
}: GuideMarkdownProps) {
  const chunks = splitOnHr(body)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);

  if (!framed) {
    return (
      <div className={cn("space-y-10", className)}>
        {chunks.map((chunk, idx) => (
          <section key={idx}>
            <MarkdownChunk text={chunk} />
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {chunks.map((chunk, idx) => (
        <SectionCard key={idx}>
          <MarkdownChunk text={chunk} />
        </SectionCard>
      ))}
    </div>
  );
}

/**
 * Render a single chunk of markdown (no horizontal rules inside).
 */
export function MarkdownChunk({ text }: { text: string }) {
  const blocks = refineBlocks(parseBlocks(text));
  return <>{blocks.map((b, i) => renderBlock(b, i))}</>;
}

// ───────────────────────────────────────────────────────────────────────
// Parsing
// ───────────────────────────────────────────────────────────────────────

type Block =
  | { kind: "h1"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "h4"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "highlightList"; items: { marker: string; body: string }[] }
  | { kind: "definitionList"; items: { term: string; body: string }[] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string }
  | { kind: "table"; headers: string[]; rows: string[][] }
  // Floating editorial primitives detected from the markdown source
  | { kind: "insightCard"; emoji: string; body: string; label?: string }
  | { kind: "quoteCard"; body: string; attribution?: string }
  | { kind: "stepFlow"; items: string[] }
  | {
      kind: "compareCard";
      title?: string;
      left: { label: string; body: string; marker?: string };
      right: { label: string; body: string; marker?: string };
    };

export function splitOnHr(text: string): string[] {
  return text.split(/\n[ \t]*-{3,}[ \t]*\n/);
}

const EMOJI_MARKER_RE =
  /^([✅❌⚡🎯🔒❤️🎤🫱🏻‍🫲🏼🎨🌱🤝🕊️🎉🏠🛑👓🔍✨💚📋🗺️🧩🔄💡🛡️🌟🎓📖💬📚🎯⚠️🤖📊📅🚀🔑🎁⚙️🧠💪🏼📝🤔💭]|🔴|🟠|🟡|🟢|🔵|🟣|⚪|⚫|🟤)\s+(.*)$/u;

function parseBlocks(text: string): Block[] {
  const lines = text.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      i++;
      continue;
    }

    // Fenced code block. The AI enrichment frequently wraps quoted Hebrew
    // speech in triple-backtick fences. If the fence has no language hint and
    // the body contains any Hebrew character, treat it as a quote so the
    // mono+border+ltr code styling doesn't mangle the prose.
    if (trimmed.startsWith("```")) {
      const lang = trimmed.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      const body = codeLines.join("\n");
      const hasHebrew = /[֐-׿]/.test(body);
      if (lang === "" && hasHebrew) {
        blocks.push({ kind: "quote", text: body });
      } else {
        blocks.push({ kind: "code", text: body });
      }
      continue;
    }

    // GFM table — header row, separator row of `---|---|`, then data rows
    if (
      trimmed.startsWith("|") &&
      i + 1 < lines.length &&
      /^\|?[\s:|-]+\|[\s:|-]+\|?$/.test(lines[i + 1].trim())
    ) {
      const headers = parseTableRow(trimmed);
      const rows: string[][] = [];
      i += 2; // skip separator
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(parseTableRow(lines[i].trim()));
        i++;
      }
      blocks.push({ kind: "table", headers, rows });
      continue;
    }

    // Heading
    const headingMatch = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2].trim();
      if (level === 1) blocks.push({ kind: "h1", text });
      else if (level === 2) blocks.push({ kind: "h2", text });
      else if (level === 3) blocks.push({ kind: "h3", text });
      else blocks.push({ kind: "h4", text });
      i++;
      continue;
    }

    // Bullet list — collect, then classify
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        const m = /^[-*]\s+(.*)$/.exec(lines[i].trim());
        if (m) items.push(m[1]);
        i++;
      }
      blocks.push(classifyBulletList(items));
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const m = /^\d+\.\s+(.*)$/.exec(lines[i].trim());
        if (m) items.push(m[1]);
        i++;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }

    // Blockquote
    if (trimmed.startsWith(">")) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", text: quoteLines.join("\n") });
      continue;
    }

    // Paragraph — accumulate lines until a structural break
    const paraLines: string[] = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (t === "") break;
      if (/^(#{1,4})\s+/.test(t)) break;
      if (/^[-*]\s+/.test(t)) break;
      if (/^\d+\.\s+/.test(t)) break;
      if (t.startsWith(">")) break;
      if (t.startsWith("```")) break;
      if (t.startsWith("|")) break;
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ kind: "p", text: paraLines.join("\n") });
  }

  return blocks;
}

/**
 * A bullet list might be:
 *   - all items prefixed with the same emoji → highlight list
 *   - all items starting with `**term**:` or `**term:**` → definition list
 *   - otherwise → plain ul
 */
function classifyBulletList(items: string[]): Block {
  if (items.length === 0) return { kind: "ul", items };

  // Highlight list: every item starts with an emoji marker
  const emojiHits = items.map((item) => EMOJI_MARKER_RE.exec(item));
  if (emojiHits.every((m) => m !== null)) {
    const highlightItems = emojiHits.map((m) => ({
      marker: m![1],
      body: m![2],
    }));
    return { kind: "highlightList", items: highlightItems };
  }

  // Definition list: every item is `**term**: body` or `**term:** body`
  const defRe = /^\*\*([^*]+?)(?::\*\*|\*\*:)\s*(.+)$/;
  const defHits = items.map((item) => defRe.exec(item));
  if (defHits.every((m) => m !== null) && items.length >= 2) {
    const defItems = defHits.map((m) => ({
      term: m![1].trim(),
      body: m![2].trim(),
    }));
    return { kind: "definitionList", items: defItems };
  }

  return { kind: "ul", items };
}

function parseTableRow(line: string): string[] {
  // Strip outer pipes, split on inner pipes, trim each cell
  const stripped = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return stripped.split("|").map((c) => c.trim());
}

/**
 * Second-pass refinement — promote plain blocks to the floating editorial
 * primitives (Insight / Quote / StepFlow / Compare) based on their shape.
 *
 * Conservative: only convert when the heuristic is confident, otherwise
 * leave the block as-is for the typography fallback.
 */
function refineBlocks(blocks: Block[]): Block[] {
  return blocks.map((b) => {
    if (b.kind === "p") return refineParagraph(b);
    if (b.kind === "quote") return refineQuote(b);
    if (b.kind === "ol") return refineOl(b);
    if (b.kind === "table") return refineTable(b);
    return b;
  });
}

function refineParagraph(b: { kind: "p"; text: string }): Block {
  const trimmed = b.text.trim();
  // Only single-line paragraphs become insight cards — multi-line prose stays
  // as <p>. The leading-emoji + length cap keeps this from hijacking
  // ordinary text.
  if (trimmed.includes("\n")) return b;
  const m = EMOJI_MARKER_RE.exec(trimmed);
  if (m && trimmed.length <= 280) {
    return { kind: "insightCard", emoji: m[1], body: m[2] };
  }
  return b;
}

function refineQuote(b: { kind: "quote"; text: string }): Block {
  const lines = b.text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return b;
  const last = lines[lines.length - 1];
  // Attribution: a final line starting with em-dash, en-dash, or "--"
  if (/^[—–]\s*\S/.test(last) || /^--\s*\S/.test(last)) {
    const attribution = last.replace(/^[—–-]+\s*/, "").trim();
    const body = lines.slice(0, -1).join(" ").trim();
    if (body) {
      return { kind: "quoteCard", body, attribution };
    }
  }
  return b;
}

function refineOl(b: { kind: "ol"; items: string[] }): Block {
  // Numbered, short list of 2–6 items → step flow. Keep longer/longer-item
  // lists as plain <ol> so multi-paragraph procedures still read naturally.
  if (b.items.length < 2 || b.items.length > 6) return b;
  if (b.items.some((it) => it.length > 80)) return b;
  return { kind: "stepFlow", items: b.items };
}

function refineTable(b: {
  kind: "table";
  headers: string[];
  rows: string[][];
}): Block {
  // 2-column table with a single data row → before/after compare card.
  if (b.headers.length !== 2) return b;
  if (b.rows.length !== 1) return b;
  const [leftBody, rightBody] = b.rows[0];
  if (!leftBody || !rightBody) return b;
  if (leftBody.length > 240 || rightBody.length > 240) return b;
  const leftM = EMOJI_MARKER_RE.exec(b.headers[0].trim());
  const rightM = EMOJI_MARKER_RE.exec(b.headers[1].trim());
  return {
    kind: "compareCard",
    left: {
      label: leftM ? leftM[2] : b.headers[0],
      body: leftBody,
      marker: leftM?.[1],
    },
    right: {
      label: rightM ? rightM[2] : b.headers[1],
      body: rightBody,
      marker: rightM?.[1],
    },
  };
}

function emojiToInsightTone(emoji: string): InsightTone {
  return (EMOJI_TO_TONE[emoji] ?? "stone") as InsightTone;
}

// ───────────────────────────────────────────────────────────────────────
// Rendering
// ───────────────────────────────────────────────────────────────────────

const EMOJI_TO_TONE: Record<string, HighlightTone> = {
  "✅": "emerald",
  "❌": "rose",
  "⚡": "amber",
  "🎯": "blue",
  "🔒": "violet",
  "❤️": "rose",
  "🎤": "blue",
  "🫱🏻‍🫲🏼": "amber",
  "🎨": "violet",
  "🌱": "emerald",
  "🤝": "blue",
  "🕊️": "blue",
  "🎉": "amber",
  "🏠": "amber",
  "🛑": "rose",
  "👓": "violet",
  "🔍": "violet",
  "✨": "amber",
  "💚": "emerald",
  "📋": "stone",
  "🗺️": "blue",
  "🧩": "violet",
  "🔄": "blue",
  "💡": "amber",
  "🛡️": "violet",
  "🌟": "amber",
  "🎓": "violet",
  "📖": "stone",
  "💬": "blue",
  "📚": "stone",
  "⚠️": "amber",
  "🤖": "stone",
  "📊": "blue",
  "📅": "blue",
  "🚀": "amber",
  "🔑": "amber",
  "🎁": "rose",
  "⚙️": "stone",
  "🧠": "violet",
  "💪🏼": "emerald",
  "📝": "stone",
  "🤔": "stone",
  "💭": "stone",
  "🔴": "rose",
  "🟠": "amber",
  "🟡": "amber",
  "🟢": "emerald",
  "🔵": "blue",
  "🟣": "violet",
  "⚪": "stone",
  "⚫": "stone",
  "🟤": "stone",
};

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case "h1":
      return (
        <h2
          key={key}
          className="text-[1.5rem] font-bold tracking-tight text-stone-900 mt-8 mb-3 first:mt-0 leading-tight"
        >
          {renderInline(block.text)}
        </h2>
      );
    case "h2":
      return (
        <h3
          key={key}
          className="text-[1.25rem] font-semibold tracking-tight text-stone-900 mt-7 mb-2 first:mt-0 leading-snug"
        >
          {renderInline(block.text)}
        </h3>
      );
    case "h3":
      return (
        <h4
          key={key}
          className="text-base font-semibold text-stone-900 mt-5 mb-2 first:mt-0"
        >
          {renderInline(block.text)}
        </h4>
      );
    case "h4":
      return (
        <h5
          key={key}
          className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500 mt-5 mb-2 first:mt-0"
        >
          {renderInline(block.text)}
        </h5>
      );
    case "p":
      return (
        <p
          key={key}
          className="text-[15px] leading-[1.85] text-stone-700 my-3 first:mt-0 last:mb-0 whitespace-pre-wrap"
        >
          {renderInline(block.text)}
        </p>
      );
    case "ul":
      return (
        <ul
          key={key}
          className="list-disc ps-6 space-y-1.5 text-[15px] leading-[1.8] text-stone-700 my-3 marker:text-stone-400"
        >
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol
          key={key}
          className="list-decimal ps-6 space-y-1.5 text-[15px] leading-[1.8] text-stone-700 my-3 marker:text-stone-400"
        >
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ol>
      );
    case "highlightList":
      return (
        <div key={key} className="my-4">
          <HighlightList
            items={block.items.map((item) => ({
              marker: item.marker,
              body: renderInline(item.body),
              tone: EMOJI_TO_TONE[item.marker] ?? "stone",
            }))}
          />
        </div>
      );
    case "definitionList":
      return (
        <div key={key} className="my-4 space-y-2">
          {block.items.map((item, i) => (
            <DefinitionRow
              key={i}
              term={item.term}
              body={renderInline(item.body)}
              layout="inline"
            />
          ))}
        </div>
      );
    case "quote":
      return (
        <PullQuote key={key} className="my-5">
          {renderInline(block.text)}
        </PullQuote>
      );
    case "code":
      return (
        <pre
          key={key}
          className="my-4 rounded-xl border border-stone-200/70 bg-stone-50 p-4 text-xs leading-relaxed font-mono text-stone-700 overflow-x-auto whitespace-pre"
          dir="ltr"
        >
          <code>{block.text}</code>
        </pre>
      );
    case "table":
      return (
        <div key={key} className="my-5">
          <MarkdownTable
            headers={block.headers.map((h, i) => (
              <span key={i}>{renderInline(h)}</span>
            ))}
            rows={block.rows.map((row) =>
              row.map((cell, i) => <span key={i}>{renderInline(cell)}</span>),
            )}
          />
        </div>
      );
    case "insightCard":
      return (
        <InsightCard
          key={key}
          emoji={block.emoji}
          tone={emojiToInsightTone(block.emoji)}
          label={block.label}
        >
          {renderInline(block.body)}
        </InsightCard>
      );
    case "quoteCard":
      return (
        <QuoteCard
          key={key}
          attribution={block.attribution ? renderInline(block.attribution) : undefined}
        >
          {renderInline(block.body)}
        </QuoteCard>
      );
    case "stepFlow":
      return (
        <StepFlow
          key={key}
          items={block.items.map((body) => ({ body: renderInline(body) }))}
        />
      );
    case "compareCard":
      return (
        <CompareCard
          key={key}
          title={block.title ? renderInline(block.title) : undefined}
          left={{
            label: renderInline(block.left.label),
            body: renderInline(block.left.body),
            tone: compareTone(block.left.marker, "rose"),
            marker: block.left.marker,
          }}
          right={{
            label: renderInline(block.right.label),
            body: renderInline(block.right.body),
            tone: compareTone(block.right.marker, "emerald"),
            marker: block.right.marker,
          }}
        />
      );
  }
}

function compareTone(
  marker: string | undefined,
  fallback: NonNullable<import("./CompareCard").CompareSide["tone"]>,
): NonNullable<import("./CompareCard").CompareSide["tone"]> {
  if (!marker) return fallback;
  const t = EMOJI_TO_TONE[marker];
  if (t === "rose" || t === "emerald" || t === "blue" || t === "violet" || t === "amber" || t === "stone") {
    return t;
  }
  return fallback;
}

function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  const tokenRe =
    /(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\*[^*\n]+\*)|(_[^_\n]+_)|(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\n]+\))/;

  while (remaining.length > 0) {
    const m = tokenRe.exec(remaining);
    if (!m) {
      nodes.push(remaining);
      break;
    }
    const idx = m.index;
    if (idx > 0) nodes.push(remaining.slice(0, idx));
    const token = m[0];

    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(
        <strong key={key++} className="font-semibold text-stone-900">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("__") && token.endsWith("__")) {
      nodes.push(
        <strong key={key++} className="font-semibold text-stone-900">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-stone-100 px-1.5 py-0.5 text-[0.85em] font-mono text-stone-800"
          dir="ltr"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        nodes.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted underline-offset-2 text-stone-900 hover:text-amber-700"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    } else if (
      (token.startsWith("*") && token.endsWith("*")) ||
      (token.startsWith("_") && token.endsWith("_"))
    ) {
      nodes.push(
        <em key={key++} className="italic">
          {token.slice(1, -1)}
        </em>,
      );
    } else {
      nodes.push(token);
    }

    remaining = remaining.slice(idx + token.length);
  }

  return nodes;
}
